import { Loader2, DollarSign, X, Mail } from "lucide-react";
import { AgingBoxes, InvoicesDuePanel, useInvoicesDue } from "@/components/customers/InvoicesDuePanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const JOURNAL_TYPES = [
  { value: "PAYMENT", label: "PAYMENT" },
  { value: "CREDIT", label: "CREDIT NOTE" },
  { value: "DEBIT", label: "DEBIT NOTE" },
  { value: "DISCOUNT", label: "DISCOUNT" },
];

export const JournalTab = ({
  formData, customer, financialSummary,
  journalForm, setJournalForm, processingJournal,
  handleProcessJournal, onClose,
}) => {
  const customerEmail = formData?.email || customer?.email || "";
  const dueData = useInvoicesDue(customer?.id, processingJournal);
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-700 to-gray-600 text-white p-4 rounded">
        <div className="flex items-center gap-2">
          <DollarSign className="h-6 w-6" />
          <h2 className="text-2xl font-bold">ACCOUNT JOURNAL</h2>
        </div>
      </div>

      {/* Customer Summary */}
      <div className="grid grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <Label className="text-gray-500">Name</Label>
            <div className="bg-gray-100 p-3 rounded text-lg font-bold">
              {formData.name}
            </div>
          </div>

          <div className="grid grid-cols-4 gap-2">
            <div>
              <Label className="text-xs text-gray-500">Account No.</Label>
              <div className="bg-gray-100 p-2 rounded text-sm font-mono">
                {formData.account_number}
              </div>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Credit Limit</Label>
              <div className="bg-gray-100 p-2 rounded text-sm font-mono">
                {(parseFloat(formData.credit_limit) || 0).toFixed(2)}
              </div>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Current Balance</Label>
              <div className="bg-gray-100 p-2 rounded text-sm font-mono">
                {(financialSummary?.balance || 0).toFixed(2)}
              </div>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Balance Owing</Label>
              <div className="bg-gray-100 p-2 rounded text-sm font-mono">
                {(financialSummary?.balance || 0).toFixed(2)}
              </div>
            </div>
            <div>
              <Label className="text-xs text-purple-600 font-medium">Loyalty Balance</Label>
              <div className="bg-purple-50 border border-purple-200 p-2 rounded text-sm font-mono text-purple-700">
                R {(formData.loyalty_credit_balance || customer?.loyalty_credit_balance || 0).toFixed(2)}
              </div>
            </div>
          </div>

          {/* Aging Buckets */}
          <AgingBoxes data={dueData} />

          {/* Journal Type Selection */}
          <div>
            <Label>Journal Type</Label>
            <Select
              value={journalForm.journal_type}
              onValueChange={(value) => setJournalForm(prev => ({ ...prev, journal_type: value }))}
            >
              <SelectTrigger className="bg-blue-100">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[...JOURNAL_TYPES].sort((a, b) => (a.label || '').localeCompare(b.label || '')).map(type => (
                  <SelectItem key={type.value} value={type.value} className="bg-yellow-100">
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div>
            <Label>Description</Label>
            <Input
              value={journalForm.description}
              onChange={(e) => setJournalForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Enter description..."
            />
          </div>
        </div>

        {/* Right side - Date picker and Amount */}
        <div className="space-y-4">
          <div>
            <Label>Date</Label>
            <Input
              type="date"
              value={journalForm.date}
              onChange={(e) => setJournalForm(prev => ({ ...prev, date: e.target.value }))}
            />
          </div>

          <div>
            <Label>Amount</Label>
            <div className="bg-black text-green-400 p-4 rounded text-right">
              <Input
                type="number"
                step="0.01"
                min="0"
                value={journalForm.amount}
                onChange={(e) => setJournalForm(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="0.00"
                className="bg-transparent border-none text-green-400 text-3xl font-mono text-right focus:ring-0"
              />
            </div>
          </div>

          {/* Email transaction confirmation to customer — opt-in, never automatic */}
          <div className="border rounded p-3 bg-blue-50 border-blue-200">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!journalForm.email_to_customer}
                disabled={!customerEmail}
                onChange={(e) => setJournalForm(prev => ({ ...prev, email_to_customer: e.target.checked }))}
                className="mt-1 rounded"
                data-testid="journal-email-to-customer-checkbox"
              />
              <span className="text-sm">
                <span className="flex items-center gap-1 font-medium text-blue-700">
                  <Mail className="h-4 w-4" />
                  Email this transaction to the customer
                </span>
                {customerEmail ? (
                  <span className="block text-xs text-gray-600 mt-1">
                    Will be sent to <span className="font-mono">{customerEmail}</span>
                    {" "}plus any additional emails configured for receipts.
                  </span>
                ) : (
                  <span className="block text-xs text-amber-700 mt-1">
                    Customer has no email address on file — add one on the Profile tab to enable this.
                  </span>
                )}
              </span>
            </label>
          </div>
        </div>
      </div>

      <InvoicesDuePanel data={dueData} />

      {/* Action Buttons */}
      <div className="flex justify-between pt-4 border-t">
        <Button variant="destructive" onClick={onClose} className="flex items-center gap-2">
          <X className="h-4 w-4" />
          CANCEL
        </Button>
        <Button 
          onClick={handleProcessJournal}
          disabled={processingJournal}
          className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
        >
          {processingJournal ? <Loader2 className="h-4 w-4 animate-spin" /> : <span className="text-xl">&#10003;</span>}
          PROCESS
        </Button>
      </div>
    </div>
  );
};
